import { MapOverlay, MapOverlayDatumContentProducer, MapItemBounds, MapDataCluster, MapLayout, MapOverlayCategory  } from '../map/map-overlay';
import { Visualizer, VisualizerControl, VisualizationUpdateLevel } from '../data-source/visualizer';
import { ModelingDataSource, ModelingSourceDatum, modelingSourceDataReader } from './modeling';
import { ModelProject } from './modeling-common';
import { ModelingVisualizerControl } from './container-modeling';
import { Timeline } from '../timeline/timeline';
import { Pixel } from '../map/map-types';
import { Size } from '../data/common';
import { getPaletteColor, colorToString } from '../util/color';
import { AnimationMode } from '../data-source/types';


export class ModelingSourceMapVisualizer extends MapOverlay implements Visualizer {
	private controls: { [key: string]: VisualizerControl }

	public constructor(private visualizerKey: string, private source: ModelingDataSource, className: string) {
		super(
			MapOverlayCategory.MODELLING_INPUT,
			className,
			{
				eventHandlers: {
					'click': (evAndTrgt, _data) => {
						evAndTrgt.event.preventDefault();
						evAndTrgt.event.stopPropagation();

						let overlayItem = (<MapOverlayDatumContentProducer>this.getProducer()).getItem(evAndTrgt.target.id);
						if (overlayItem) {
							source.getApplication().getInspectionPanel().switchContainer(overlayItem.group).then(_containerPanel => {
								source.onSelection(overlayItem.group, overlayItem.item.dataItemIds);
							});
						}
					}
				},
				elementData: {
					'source': source.getName(),
					'visualizer': visualizerKey
				}
			},
			new MapOverlayDatumContentProducer(
				{
					size: () => { return { width: 15, height: 15, border: 2 }; },
					draw: (domElem, meta, _size: Size, _localProjection) => {
						// dataItem0 is first datum in cluster
						let dataItem0 = <ModelingSourceDatum>meta.dataItems[meta.dataItemIds[0]];
						//            let typeDefinition = <ObservationTypeDefinition>source.getDefinitions()[dataItem0.type];
						let projectReference = dataItem0.projects[0];
						let dataList = source.getData()[projectReference.type];
						let modelProject = <ModelProject>dataList.get(projectReference.id);

						let baseColor = getPaletteColor(0, modelProject.parameters.render.hue);
						domElem.style.color = colorToString(baseColor);
						domElem.style.lineHeight = `${meta.widthPX}px`;
						domElem.style.fontSize = `${meta.widthPX}px`;


						// add div for the number of underlying observations when count greater than 1
						if (meta.count > 1) {
							domElem.innerHTML = `<div class="tlmd-marker-label"><div>${meta.count}</div></div>`
						}

					},
					overlap: (item0: MapItemBounds, item1: MapItemBounds): boolean => {
						//this renderer assumes circles
						let c0x = item0.lngPX;
						let c0y = item0.latPX;

						let c1x = item1.lngPX
						let c1y = item1.latPX;


						let dx = c0x - c1x;
						let dy = c0y - c1y;
						let dPOW2 = dx * dx + dy * dy;
						let r0 = item0.widthPX / 2;
						let r1 = item1.widthPX / 2;
						let r0PLUSr1 = r0 + r1;

						return (dPOW2 < r0PLUSr1 * r0PLUSr1);
					},
					merge: (clusters: MapDataCluster[], minimimSize: Size): MapDataCluster => {
						if (clusters && clusters.length > 0) {
							if (clusters.length > 1) {
								let growingCluster = clusters[0];
								let clusterBounds = growingCluster.bounds.reduce((bounds, mpItemBounds) => {
									bounds.ne.x = Math.max(bounds.ne.x, mpItemBounds.eastPX);
									bounds.ne.y = Math.min(bounds.ne.y, mpItemBounds.northPX);
									bounds.sw.x = Math.min(bounds.sw.x, mpItemBounds.westPX);
									bounds.sw.y = Math.max(bounds.sw.y, mpItemBounds.southPX);
									return bounds;
								}, {
									ne: <Pixel>{ x: growingCluster.eastPX, y: growingCluster.northPX },
									sw: <Pixel>{ x: growingCluster.westPX, y: growingCluster.southPX }
								});

								let width = clusterBounds.ne.x - clusterBounds.sw.x;
								let height = clusterBounds.sw.y - clusterBounds.ne.y;

								growingCluster.lngPX = clusterBounds.sw.x + width / 2;
								growingCluster.latPX = clusterBounds.ne.y + height / 2;

								let size = Math.max(minimimSize.width, width, height);
								growingCluster.widthPX = size;
								growingCluster.heightPX = size;
								growingCluster.northPX = growingCluster.latPX - size / 2;
								growingCluster.southPX = growingCluster.northPX + size;
								growingCluster.westPX = growingCluster.lngPX - size / 2;
								growingCluster.eastPX = growingCluster.westPX + size;

								growingCluster.east = undefined;
								growingCluster.west = undefined;
								growingCluster.north = undefined;
								growingCluster.south = undefined;
								growingCluster.lat = undefined;
								growingCluster.lng = undefined;

								return growingCluster;

							} else {
								return clusters[0];
							}
						} else {
							return undefined;
						}

					}

				},
				modelingSourceDataReader,
				{
					layout: MapLayout.overlap
				})
		)

	}

	public key(): string {
		return this.visualizerKey;
	}

	public type(): string {
		//this visualizer is multi type
		return undefined;
	}

	public setControls(controls: { [key: string]: VisualizerControl }): Visualizer {
		this.controls = <{ [key: string]: ModelingVisualizerControl }>controls;
		return this;
	}

	public release(): void {
	}

	public setTimeline(_timeline: Timeline): void {
		//we do not deal with the timeline here
	}

	public updateSelection(): void {
		//not implemented (yet??)
	}

	public refresh(_level: VisualizationUpdateLevel): void {
		if (this.controls) {

			(<MapOverlayDatumContentProducer>this.getProducer()).setAnimationPosition(this.source.getTimeline().getCursorMillis(true));

			let types = Object.entries(this.controls)
				.filter(controlEntry => controlEntry[1].enabled(this.key()))
				.map(controlEntry => controlEntry[0]);

			if (_level <= VisualizationUpdateLevel.filter) {
				let dataMap = this.source.getModelingSourceData(types);
				(<MapOverlayDatumContentProducer>this.getProducer()).setData(dataMap);
			}


			let typesAndModes = Object.entries(this.controls)
				.filter(controlEntry => controlEntry[1].enabled(this.key()))
				.reduce((accu, control) => {
					accu.types.push(control[0]);
					accu.modes[control[0]] = control[1].animationMode(this.key());
					return accu;
				}, <{ types: string[], modes: { [key: string]: AnimationMode } }>{ types: [], modes: {} })



			if (_level <= VisualizationUpdateLevel.filter) {
				let dataMap = this.source.getModelingSourceData(typesAndModes.types);
				(<MapOverlayDatumContentProducer>this.getProducer()).setData(dataMap);
			}

			(<MapOverlayDatumContentProducer>this.getProducer()).setAnimationModes(typesAndModes.modes);

			this.update();


		}
	}

}

